import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import {
  ArrowLeft, Users, Activity, FileText, Clock, ChevronRight,
  CheckCircle, MessageSquare, Eye, Settings, Plus, Shield
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  idea: { bg: '#FEF3C7', text: '#92400E', label: 'Idea' },
  confirmed: { bg: '#D1FAE5', text: '#065F46', label: 'Confirmed' },
  active: { bg: '#DBEAFE', text: '#1E40AF', label: 'Active' },
  completed: { bg: '#F3F4F6', text: '#6B7280', label: 'Completed' },
};

const ROLE_STYLES = {
  admin: { label: 'Admin', color: '#7A8F5B' },
  manager: { label: 'Manager', color: '#E8A87C' },
  staff: { label: 'Staff', color: '#7FA5B8' },
  member: { label: 'Member', color: '#9CA3AF' },
};

export default function InstitutionDashboard() {
  const { institutionId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [dashboard, setDashboard] = useState(null);
  const [members, setMembers] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (user) loadDashboard();
  }, [institutionId, user]);

  const loadDashboard = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [dashRes, membersRes, docsRes] = await Promise.all([
        axios.get(`${API}/api/inst-admin/${institutionId}/dashboard`, { headers }),
        axios.get(`${API}/api/inst-admin/${institutionId}/members`, { headers }),
        axios.get(`${API}/api/documents/institution/${institutionId}`, { headers }).catch(() => ({ data: { documents: [] } })),
      ]);
      setDashboard(dashRes.data);
      setMembers(membersRes.data.members || []);
      setDocuments(docsRes.data.documents || []);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('You do not have access to this dashboard');
        navigate(-1);
      } else {
        toast.error('Failed to load dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLifecycle = async (activityId, newStatus) => {
    // Completion requires explicit confirmation
    if (newStatus === 'completed') {
      if (!window.confirm('Mark this activity as completed?\n\nThis confirms the activity actually occurred. Participation will be frozen.')) {
        return;
      }
    }
    try {
      const token = localStorage.getItem('token');
      const payload = { status: newStatus };
      if (newStatus === 'completed') {
        payload.confirm_completion = true;
      }
      await axios.put(
        `${API}/api/inst-admin/${institutionId}/activities/${activityId}/lifecycle`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Status updated to ${newStatus}`);
      loadDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  const handlePublish = async (activityId, published) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/api/inst-admin/${institutionId}/activities/${activityId}/publish`,
        { published },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(published ? 'Published' : 'Unpublished');
      loadDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/api/inst-admin/${institutionId}/members/${userId}/role`,
        { role: newRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Role updated');
      loadDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  if (!user) { navigate('/auth'); return null; }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: 'var(--lk-sage)' }} />
      </div>
    );
  }

  if (!dashboard) return null;

  const { institution, role, overview, upcoming_activities, recent_messages, status_breakdown } = dashboard;
  const canManageActivities = ['admin', 'manager'].includes(role);
  const canManageRoles = role === 'admin';
  const canManageDocs = ['admin', 'manager'].includes(role);
  const canViewOnly = role === 'staff';

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'activities', label: 'Activities' },
    { id: 'calendar', label: 'Calendar' },
    { id: 'members', label: 'Members' },
    { id: 'documents', label: 'Documents' },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      {/* Header */}
      <header className="sticky top-0 z-50" style={{ backgroundColor: 'var(--fy-surface-card)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-3xl mx-auto px-4">
          <div className="flex items-center h-14 gap-3">
            <button onClick={() => navigate(-1)} className="p-2 rounded-full" data-testid="dashboard-back-btn">
              <ArrowLeft className="w-5 h-5" style={{ color: 'var(--fy-text-primary)' }} />
            </button>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-bold truncate" style={{ color: 'var(--fy-text-primary)' }} data-testid="dashboard-title">
                {institution.name}
              </h1>
              <div className="flex items-center gap-2">
                <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: `${ROLE_STYLES[role]?.color || '#9CA3AF'}20`, color: ROLE_STYLES[role]?.color }}>
                  {ROLE_STYLES[role]?.label || role}
                </span>
                <span className="text-xs" style={{ color: 'var(--fy-text-disabled)' }}>{institution.type}</span>
              </div>
            </div>
            {canManageActivities && (
              <Button
                size="sm"
                className="rounded-full"
                style={{ backgroundColor: 'var(--lk-sage)', color: 'white' }}
                onClick={() => navigate(`/activities/create?source_type=institution&source_id=${institutionId}`)}
                data-testid="create-activity-btn"
              >
                <Plus className="w-4 h-4 mr-1" /> Activity
              </Button>
            )}
          </div>

          {/* Tabs */}
          <div className="flex gap-1 pb-1 overflow-x-auto">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className="px-3 py-2 text-sm font-medium rounded-lg whitespace-nowrap transition-all"
                style={{
                  backgroundColor: activeTab === tab.id ? 'var(--lk-sage-muted, #EDF1E8)' : 'transparent',
                  color: activeTab === tab.id ? 'var(--lk-sage)' : 'var(--fy-text-secondary)',
                }}
                data-testid={`tab-${tab.id}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-5 space-y-4">
        {/* ===== OVERVIEW TAB ===== */}
        {activeTab === 'overview' && (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="overview-stats">
              <StatCard icon={Users} label="Members" value={overview.members} color="var(--lk-sage)" />
              <StatCard icon={Activity} label="Active" value={overview.active_activities} color="var(--lk-secondary-orange)" />
              <StatCard icon={FileText} label="Documents" value={overview.documents} color="#7FA5B8" />
              <StatCard icon={CheckCircle} label="Pending Acks" value={overview.pending_acknowledgements} color="#8B5CF6" />
            </div>

            {/* Activity Status Breakdown */}
            {Object.keys(status_breakdown).length > 0 && (
              <Card className="p-4" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
                <p className="text-xs font-semibold mb-3" style={{ color: 'var(--fy-text-secondary)' }}>Activity Status</p>
                <div className="flex gap-2 flex-wrap">
                  {Object.entries(status_breakdown).map(([status, count]) => {
                    const s = STATUS_STYLES[status] || STATUS_STYLES.idea;
                    return (
                      <span key={status} className="px-3 py-1 rounded-full text-xs font-medium"
                        style={{ backgroundColor: s.bg, color: s.text }}>
                        {s.label}: {count}
                      </span>
                    );
                  })}
                </div>
              </Card>
            )}

            {/* Upcoming Activities */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold" style={{ color: 'var(--fy-text-primary)' }}>Upcoming Activities</p>
                <button onClick={() => setActiveTab('activities')} className="text-xs" style={{ color: 'var(--lk-sage)' }}>View all</button>
              </div>
              {upcoming_activities.length === 0 ? (
                <Card className="p-4 text-center" style={{ backgroundColor: 'var(--fy-surface-muted)' }}>
                  <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>No upcoming activities</p>
                </Card>
              ) : (
                <div className="space-y-2">
                  {upcoming_activities.slice(0, 5).map(act => (
                    <DashboardActivityCard
                      key={act.activity_id}
                      activity={act}
                      canManage={canManageActivities}
                      onLifecycle={handleLifecycle}
                      onPublish={handlePublish}
                      onOpen={() => navigate(`/activities/${act.activity_id}`)}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Recent Messages */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold" style={{ color: 'var(--fy-text-primary)' }}>Recent Messages</p>
                <button onClick={() => navigate(`/institutions/${institutionId}/messages`)} className="text-xs" style={{ color: 'var(--lk-sage)' }}>View all</button>
              </div>
              {recent_messages.length === 0 ? (
                <Card className="p-4 text-center" style={{ backgroundColor: 'var(--fy-surface-muted)' }}>
                  <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>No recent messages</p>
                </Card>
              ) : (
                <div className="space-y-2">
                  {recent_messages.slice(0, 3).map((msg, i) => (
                    <Card key={i} className="p-3" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
                      <p className="text-sm font-medium" style={{ color: 'var(--fy-text-primary)' }}>{msg.subject || msg.title || 'Message'}</p>
                      <p className="text-xs mt-1" style={{ color: 'var(--fy-text-secondary)' }}>
                        {msg.created_at ? new Date(msg.created_at).toLocaleDateString() : ''}
                      </p>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* ===== ACTIVITIES TAB ===== */}
        {activeTab === 'activities' && (
          <div className="space-y-2">
            {canManageActivities && (
              <Card
                className="p-4 cursor-pointer"
                onClick={() => navigate(`/activities/create?source_type=institution&source_id=${institutionId}`)}
                style={{ backgroundColor: 'var(--lk-sage-muted, #EDF1E8)', border: '1px solid var(--lk-sage)' }}
                data-testid="create-activity-card"
              >
                <div className="flex items-center gap-3">
                  <Plus className="w-5 h-5" style={{ color: 'var(--lk-sage)' }} />
                  <p className="font-medium" style={{ color: 'var(--lk-sage)' }}>Create Activity</p>
                </div>
              </Card>
            )}
            {upcoming_activities.length === 0 ? (
              <Card className="p-6 text-center" style={{ backgroundColor: 'var(--fy-surface-muted)' }}>
                <Activity className="w-8 h-8 mx-auto mb-2" style={{ color: 'var(--fy-text-disabled)' }} />
                <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>No activities yet</p>
              </Card>
            ) : (
              upcoming_activities.map(act => (
                <DashboardActivityCard
                  key={act.activity_id}
                  activity={act}
                  canManage={canManageActivities}
                  onLifecycle={handleLifecycle}
                  onPublish={handlePublish}
                  onOpen={() => navigate(`/activities/${act.activity_id}`)}
                  expanded
                />
              ))
            )}
          </div>
        )}

        {/* ===== CALENDAR TAB ===== */}
        {activeTab === 'calendar' && (
          <div className="text-center py-6">
            <p className="text-sm mb-3" style={{ color: 'var(--fy-text-secondary)' }}>
              View all scheduled activities for this institution.
            </p>
            <button
              onClick={() => navigate(`/community-calendar?scope=institution&scope_id=${institutionId}`)}
              className="px-4 py-2 rounded-lg text-sm font-medium"
              style={{ backgroundColor: 'var(--lk-primary-green)', color: '#fff' }}
              data-testid="institution-calendar-link"
            >
              Open Institution Calendar
            </button>
          </div>
        )}

        {/* ===== MEMBERS TAB ===== */}
        {activeTab === 'members' && (
          <div className="space-y-2">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold" style={{ color: 'var(--fy-text-primary)' }}>{members.length} Members</p>
            </div>
            {members.map(m => (
              <Card key={m.user_id} className="p-3 flex items-center gap-3" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
                <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold"
                  style={{ backgroundColor: `${ROLE_STYLES[m.role]?.color || '#9CA3AF'}20`, color: ROLE_STYLES[m.role]?.color }}>
                  {(m.name || '?')[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate" style={{ color: 'var(--fy-text-primary)' }}>{m.name || m.username || 'Member'}</p>
                  <span className="text-xs px-2 py-0.5 rounded-full inline-block"
                    style={{ backgroundColor: `${ROLE_STYLES[m.role]?.color || '#9CA3AF'}15`, color: ROLE_STYLES[m.role]?.color }}>
                    {ROLE_STYLES[m.role]?.label || m.role}
                  </span>
                </div>
                {canManageRoles && m.role !== 'admin' && (
                  <select
                    value={m.role}
                    onChange={(e) => handleRoleChange(m.user_id, e.target.value)}
                    className="text-xs px-2 py-1 rounded border outline-none"
                    style={{ borderColor: 'var(--fy-border-subtle)', color: 'var(--fy-text-primary)', backgroundColor: 'var(--fy-surface-card)' }}
                    data-testid={`role-select-${m.user_id}`}
                  >
                    <option value="manager">Manager</option>
                    <option value="staff">Staff</option>
                    <option value="member">Member</option>
                  </select>
                )}
              </Card>
            ))}
          </div>
        )}

        {/* ===== DOCUMENTS TAB ===== */}
        {activeTab === 'documents' && (
          <div className="space-y-2">
            {canManageDocs && (
              <Card
                className="p-4 cursor-pointer"
                onClick={() => navigate(`/institutions/${institutionId}/create-document`)}
                style={{ backgroundColor: 'var(--fy-surface-muted)', border: '1px dashed var(--fy-border-subtle)' }}
                data-testid="create-document-card"
              >
                <div className="flex items-center gap-3">
                  <Plus className="w-5 h-5" style={{ color: 'var(--fy-text-secondary)' }} />
                  <p className="text-sm font-medium" style={{ color: 'var(--fy-text-secondary)' }}>Create Document</p>
                </div>
              </Card>
            )}
            {documents.length === 0 ? (
              <Card className="p-6 text-center" style={{ backgroundColor: 'var(--fy-surface-muted)' }}>
                <FileText className="w-8 h-8 mx-auto mb-2" style={{ color: 'var(--fy-text-disabled)' }} />
                <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>No documents yet</p>
              </Card>
            ) : (
              documents.map(doc => (
                <Card key={doc.document_id} className="p-3" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate" style={{ color: 'var(--fy-text-primary)' }}>{doc.title}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>{doc.document_type}</span>
                        {doc.requires_acknowledgement && (
                          <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}>
                            {doc.acknowledgement_count || 0} acknowledged
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => navigate(`/documents/${doc.document_id}`)}
                      className="p-2"
                      data-testid={`view-doc-${doc.document_id}`}
                    >
                      <Eye className="w-4 h-4" style={{ color: 'var(--fy-text-secondary)' }} />
                    </button>
                  </div>
                </Card>
              ))
            )}
          </div>
        )}
      </main>
    </div>
  );
}


function StatCard({ icon: Icon, label, value, color }) {
  return (
    <Card className="p-3" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
      <Icon className="w-4 h-4 mb-1" style={{ color }} />
      <p className="text-xl font-bold" style={{ color: 'var(--fy-text-primary)' }}>{value}</p>
      <p className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>{label}</p>
    </Card>
  );
}


function DashboardActivityCard({ activity, canManage, onLifecycle, onPublish, onOpen, expanded }) {
  const status = activity.lifecycle_status || 'idea';
  const s = STATUS_STYLES[status] || STATUS_STYLES.idea;
  const startTime = activity.start_time ? new Date(activity.start_time) : null;
  const nextStatus = { idea: 'confirmed', confirmed: 'active', active: 'completed' }[status];
  const vis = activity.visibility || 'public';

  const VIS_LABELS = {
    public: { label: 'Public', bg: '#DBEAFE', text: '#1E40AF' },
    institution: { label: 'Institution Only', bg: '#EDE9FE', text: '#5B21B6' },
    private: { label: 'Private', bg: '#F3F4F6', text: '#6B7280' },
  };
  const vl = VIS_LABELS[vis] || VIS_LABELS.public;

  return (
    <Card className="p-3" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ backgroundColor: s.bg, color: s.text }}>
              {s.label}
            </span>
            <span className="px-2 py-0.5 rounded-full text-[10px] font-medium" style={{ backgroundColor: vl.bg, color: vl.text }} data-testid={`vis-badge-${activity.activity_id}`}>
              {vl.label}
            </span>
            {activity.is_published === false && (
              <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ backgroundColor: '#FEE2E2', color: '#991B1B' }}>
                Unpublished
              </span>
            )}
          </div>
          <p className="font-medium text-sm truncate cursor-pointer" onClick={onOpen} style={{ color: 'var(--fy-text-primary)' }}>
            {activity.title}
          </p>
          <div className="flex items-center gap-3 mt-1 text-xs" style={{ color: 'var(--fy-text-secondary)' }}>
            {startTime && (
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {startTime.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Users className="w-3 h-3" />
              {activity.participant_count || activity.participants?.length || 0}
            </span>
            {activity.created_by_name && (
              <span className="text-xs truncate" style={{ color: 'var(--fy-text-disabled)' }}>
                by {activity.created_by_name}
              </span>
            )}
          </div>
        </div>

        {canManage && (
          <div className="flex flex-col gap-1 items-end">
            {nextStatus && (
              <button
                onClick={() => onLifecycle(activity.activity_id, nextStatus)}
                className="text-[10px] px-2 py-1 rounded-full font-medium"
                style={{ backgroundColor: 'var(--lk-sage-muted, #EDF1E8)', color: 'var(--lk-sage)' }}
                data-testid={`advance-${activity.activity_id}`}
              >
                {nextStatus === 'confirmed' ? 'Confirm' : nextStatus === 'active' ? 'Start' : 'Complete'}
              </button>
            )}
            <button
              onClick={() => onPublish(activity.activity_id, activity.is_published === false)}
              className="text-[10px] px-2 py-1 rounded-full"
              style={{ color: 'var(--fy-text-disabled)' }}
              data-testid={`publish-${activity.activity_id}`}
            >
              {activity.is_published === false ? 'Publish' : 'Unpublish'}
            </button>
          </div>
        )}
      </div>
    </Card>
  );
}
